/* eslint-disable jsx-a11y/anchor-is-valid */
//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//
import ObjectID from 'bson-objectid';
import cx from 'classnames';
import { cloneDeep, delay, each, first, map, remove } from 'lodash';
import { forwardRef, ReactElement, useEffect, useImperativeHandle, useState } from 'react';

import { ReactComponent as ChevronExpand } from 'assets/icons/chevron-expand.svg';
import { Button } from 'design-system';

import {
  ConditionValue,
  FilterValue,
  isConditionValue,
  LeftValue,
  RightOption,
  PicklistValue,
  PicklistValues,
} from 'components/inputs/types';
import AppConstants from 'utils/AppConstants';
import { tNamespaced } from 'utils/i18nUtil';

import FilterCondition, { FilterConditionProps, FilterConditionValue } from './FilterCondition';

import './Filter.less';

export const OPERATOR = {
  AND: 'AND',
  OR: 'OR',
};

// Max filter condition level the backend support
const MAX_LEVEL = 3;

const tn = tNamespaced('Filter');

/* simple validation to determine if this is an empty predicate */
const predicateIsEmpty = (predicate: ConditionValue) => {
  if (isConditionValue(predicate)) {
    return [predicate.left, predicate.operator, predicate.right].every((value) => typeof value === 'undefined');
  }

  // not a predicate?
  return false;
};

const _getNewPredicate = (): ConditionValue => {
  return {
    left: undefined,
    operator: undefined,
    right: undefined,
    predicateId: ObjectID.generate(),
  };
};

const makePredicateFromFilterCondition = (condition: ConditionValue = {}) => {
  return {
    ..._getNewPredicate(),
    ...condition,
  };
};

const _getNewGroup = (predicates?: FilterValue['predicates']) => {
  predicates = predicates || [_getNewPredicate()];
  return {
    predicates,
    groupPredicateId: ObjectID.generate(),
    operator: OPERATOR.AND,
  };
};

export type ExternalOnChangeHandler = (name: string, key: string, value: FilterValue) => void; // External onChange
export type InternalOnChangeHandler = (value: FilterConditionValue, _name?: string, filterValue?: FilterValue) => void; // Recursive onChange

export interface BaseFilterProps {
  /**
   * Name of the filter. Name be on value of change event
   */
  name: string;
  /**
   * Display mode
   */
  displayMode?: string;
  /**
   * Value of the filter
   */
  value?: Partial<FilterValue>;
  /**
   * Similar functionality with value
   */
  defaultValue?: FilterValue;
  /**
   * Internal: Filter can be nested and this is the level it is in. This is
   * used internally and should not be set from outside of the component
   */
  level?: number;
  /**
   * className that will be added to the component
   */
  className?: string;
  /**
   * Handler when a picklist values is needed to be fetched
   */
  fetchPicklistValues: FilterConditionProps['fetchPicklistValues'];
  /**
   * List of picklist values that the picklist inputs in the filter will pull their respective picklist values
   */
  picklistValues: PicklistValues<PicklistValue[]>;
  /**
   * Picklist value that will be used for the first picklist (AKA Field picklist)
   */
  fieldValues: LeftValue[];
  /**
   * Use this a dependantType for getting the picklist values of the operator value. 'Operator' by default.
   */
  operatorType?: string;
  /**
   * Use this a dependantType for getting the picklist values of the right value. 'Right' by default.
   */
  rightType?: string;
  /**
   * Hides the add filter and delete button when turned on
   */
  singleCondition?: boolean;
  /**
   * Render type of the left hand side input
   */
  leftRenderType?: string;
  lhsDisabled?: boolean;
  allowUserToken?: boolean;
  rightValues?: RightOption[];
  isAllDisabled?: boolean;
  /**
   * Context string to be passed to picklist API calls (e.g., 'datastudio')
   */
  context?: string;
  hideLeftInput?: boolean;
  verticalLayout?: boolean;
  placeHolder?: string;
  fieldValueOptions?: JSX.Element[];
  /** Forces the data-token selector off regardless of the left field's datatype - for contexts with no node to pull tokens from (e.g. schema studio's validation rules) */
  disableTokens?: boolean;
  /** Suppresses the "nest condition" icon at every level, keeping the filter flat (single AND/OR group, no sub-groups) */
  disableNesting?: boolean;
  /** Keeps a row's delete icon visible even when it's the sole remaining root-level condition (normally suppressed - see the single-predicate branch below) */
  alwaysShowDelete?: boolean;
  /**
   * Normally only a *nested* group can ever reach "0 predicates" (the root level always
   * auto-refills an empty one - see _onDelete), which is what lets a nested group's delete
   * cascade up via onDelete(undefined, groupPredicateId). Set this to let that same cascade
   * happen at the root level too, e.g. so a caller embedding this Filter inside its own
   * removable row (Merge Studio's Find Duplicates/Select Winner composite rows) can remove
   * that whole row when the user deletes the sole condition, instead of it just being cleared.
   */
  cascadeDeleteAtRoot?: boolean;
}

export interface FilterProps extends BaseFilterProps {
  /**
   * Use the external onChange: (name: string, key: string, value: FilterValue) => void)
   * TODO: Separate the external and internal recursive onChange.
   *       Refactor to add another layer so that the recursive props will not be exposed.
   */
  onChange: ExternalOnChangeHandler;
  /**
   * Called when this root-level Filter's predicates become empty - only reachable when
   * cascadeDeleteAtRoot is set, since otherwise the root always auto-refills an empty
   * predicate instead of ever truly emptying out. Optional and unused by default.
   */
  onDelete?: (predicateId?: string, groupPredicateId?: string) => void;
}

export interface InternalFilterProps extends BaseFilterProps {
  /**
   * Use the external onChange: (name: string, key: string, value: FilterValue) => void)
   * TODO: Separate the external and internal recursive onChange.
   *       Refactor to add another layer so that the recursive props will not be exposed.
   */
  onChange: InternalOnChangeHandler;
  /**
   * Internal: called when a condition is deleted
   */
  onDelete: (predicateId?: string, groupPredicateId?: string) => void;
}

export interface FilterRef {
  addFilterCondition: (filterCondition: ConditionValue, replaceIfEmpty: boolean) => void;
}

const Filter = forwardRef<FilterRef, FilterProps | InternalFilterProps>(
  (
    {
      name,
      displayMode,
      value,
      defaultValue,
      level = 1,
      onChange,
      onDelete,
      className,
      picklistValues,
      fetchPicklistValues,
      operatorType,
      rightType,
      fieldValues,
      singleCondition,
      leftRenderType,
      lhsDisabled,
      allowUserToken,
      rightValues,
      context,
      hideLeftInput,
      verticalLayout,
      isAllDisabled,
      placeHolder,
      fieldValueOptions,
      disableTokens,
      disableNesting,
      alwaysShowDelete,
      cascadeDeleteAtRoot,
    },
    ref
  ) => {
    const initializePredicates = (val: FilterProps['value'], defaultVal: FilterProps['defaultValue']) => {
      let initialValue;

      if (val) {
        initialValue = val;
      } else if (defaultVal) {
        initialValue = defaultVal;
      } else {
        initialValue = _getNewGroup(undefined);
      }

      return initialValue;
    };

    // TODO: We are mixing predicates, keep this any for now. Refactor this later...
    const [predicates, setPredicates] = useState<any>(() => initializePredicates(value, defaultValue));
    const [operator, setOperator] = useState(() => predicates?.operator || OPERATOR.AND);

    const isReadonly = displayMode === AppConstants.INPUT_DISPLAY_MODE.READONLY;

    useEffect(() => {
      if (!value || !defaultValue) {
        const newPredicates = initializePredicates(value, defaultValue);
        setPredicates(newPredicates);
        setOperator(newPredicates?.operator || OPERATOR.AND);
      }
    }, [value, defaultValue]);

    const _getAddGroupCondition = () => {
      if (!isReadonly && !isAllDisabled && !singleCondition) {
        return (
          <div className="btn-filter-add-group">
            <Button
              variant="tertiary"
              size="small"
              iconLeft="actionable/add"
              className="btn-add-condition"
              onClick={() => _onAddSibling()}>
              {tn('add_condition_label')}
            </Button>
          </div>
        );
      }
    };

    const _onChange = (value: FilterConditionValue, _name?: string, filterValue?: FilterValue) => {
      let newValue = true;
      let _predicates;
      // A nested group that's shrunk down to a single child (e.g. its own delete icon removed
      // a sibling) is a pointless wrapper - promote that sole child directly into this level
      // instead of keeping it wrapped, so it isn't permanently stuck one level deeper than it
      // looks and doesn't silently swallow the next "add condition" into that dead group.
      const collapsedChild =
        filterValue?.groupPredicateId && filterValue.predicates?.length === 1 ? filterValue.predicates[0] : undefined;
      // From a filter or from a group of filters. String if from a filter
      let changeValue: FilterConditionValue | FilterValue = filterValue?.groupPredicateId
        ? collapsedChild ?? filterValue
        : value;

      if (value || filterValue) {
        // bug on the onchange of the new group that duplicates that values of the filters in the group
        _predicates = map(predicates?.predicates, (fVal) => {
          if (filterValue?.groupPredicateId && fVal.groupPredicateId === filterValue.groupPredicateId) {
            newValue = false;
            return cloneDeep(changeValue);
          }
          if (value?.predicateId && fVal.predicateId === value.predicateId) {
            newValue = false;
            return cloneDeep(value);
          }
          return fVal;
        });
        if (newValue) {
          _predicates.push(changeValue);
        }
      } else {
        _predicates = predicates.predicates;
      }

      const values = {
        predicates: cloneDeep(_predicates),
        groupPredicateId: predicates?.groupPredicateId,
        operator,
      };
      setPredicates(values);

      // TODO: Need to process group filters
      if (onChange) {
        onChange(name, name, cloneDeep(values));
      }
    };

    // Per design: clicking the AND/OR badge toggles immediately between the two -
    // there's no dropdown of options, since AND/OR is the only choice.
    const toggleGroupOperator = () => {
      const newOperator = operator === OPERATOR.OR ? OPERATOR.AND : OPERATOR.OR;
      setOperator(newOperator);
      if (onChange) {
        onChange(
          name,
          name,
          cloneDeep({
            ...predicates,
            operator: newOperator,
          })
        );
      }
    };

    // Always appends a new plain sibling predicate to THIS group - never nests. This is the
    // only thing the "+ Add Condition" button (and the addFilterCondition ref API) ever does.
    const _onAddSibling = (filterCondition?: ConditionValue, replaceIfEmpty?: boolean) => {
      const newPredicate = makePredicateFromFilterCondition(filterCondition);
      const existing = predicates?.predicates ?? [];

      // If we've provided a new filterCondition and there's only 1 (empty) predicate, replace it
      // rather than adding alongside it.
      const newPredicates =
        replaceIfEmpty && existing.length === 1 && predicateIsEmpty(existing[0])
          ? [newPredicate]
          : existing.concat(newPredicate);

      const updatedPredicates = {
        ...predicates,
        predicates: newPredicates,
      };

      setPredicates(updatedPredicates);
      onChange(name, name, updatedPredicates);
    };

    // Always wraps the specific predicate (by id) + a new empty predicate into a nested group.
    // This is the only thing a row's own nest icon ever does - it always receives a real,
    // existing predicateId, so there's no ambiguity to resolve here.
    const _onNestCondition = (predicateId: string) => {
      const existing = predicates?.predicates ?? [];
      const newPredicate = _getNewPredicate();

      const newPredicates = existing.map((predicate: FilterConditionValue) =>
        predicate.predicateId === predicateId ? _getNewGroup([predicate, newPredicate]) : predicate
      );

      const updatedPredicates = {
        ...predicates,
        predicates: newPredicates,
      };

      setPredicates(updatedPredicates);
      onChange(name, name, updatedPredicates);
    };

    const _onDelete = (predicateId?: string, groupPredicateId?: string) => {
      let _predicates = cloneDeep(predicates?.predicates);

      if (predicateId) {
        remove(_predicates, (predicate: FilterConditionValue) => {
          return predicate.predicateId === predicateId;
        });
      }

      if (groupPredicateId) {
        remove(_predicates, (predicate: FilterValue) => {
          return predicate.groupPredicateId === groupPredicateId;
        });
      }

      // Only prefill an empty condition when its at the root level - unless cascadeDeleteAtRoot
      // opts out of that, letting the root reach a genuine "0 predicates" state the same way a
      // nested group already can, so the onDelete cascade below fires here too.
      // Remove any empty grouped predicate
      if (_predicates?.length <= 0 && level <= 1 && !cascadeDeleteAtRoot) {
        _predicates.push(_getNewPredicate());
      }

      let newOperator = operator;
      // If this leaves exactly one child and that child is itself a nested group, unwrap it -
      // adopt its predicates/operator directly rather than leaving a redundant single-child
      // group behind, which would otherwise silently keep nesting new conditions into it.
      if (_predicates.length === 1 && _predicates[0]?.groupPredicateId) {
        newOperator = _predicates[0].operator;
        _predicates = _predicates[0].predicates;
      }

      const newPredicates = cloneDeep({
        ...predicates,
        predicates: _predicates,
        operator: newOperator,
      });

      setPredicates(newPredicates);
      setOperator(newOperator);

      const _groupPredicateId = predicates.groupPredicateId;

      // TODO: remove delay? Maybe use an effect instead
      delay(() => {
        if (onChange) {
          onChange(name, name, newPredicates);
        }
        if (_predicates.length <= 0 && onDelete) {
          onDelete(undefined, _groupPredicateId);
        }
      }, 10);
    };

    const _getGroupOperator = () => {
      if (isReadonly) {
        return;
      }

      return (
        <div className="filter-operator-container">
          <button
            type="button"
            className="filter-operator-toggle"
            onClick={toggleGroupOperator}
            disabled={isAllDisabled}
            aria-label={tn('toggle_operator_label')}>
            {operator} <ChevronExpand width={13} height={13} />
          </button>
        </div>
      );
    };

    // Access these methods via ref
    useImperativeHandle(ref, () => ({
      addFilterCondition: (filterCondition: ConditionValue, replaceIfEmpty: boolean) =>
        _onAddSibling(filterCondition, replaceIfEmpty),
    }));

    // TODO: Not ideal, performace trashing. Refactor this
    const cls = cx('synri-filter-condition-group', 'synri-filter-v2', className);
    const filters: ReactElement[] = [];

    // An empty group must never fall through to the single-predicate branch below: with no
    // predicate to render it mounts a blank FilterCondition, whose Condition immediately reports
    // its empty value back up as a change, which re-adds a predicate, which renders again... an
    // endless loop that also swallows any delete cascade mid-flight.
    if (!predicates?.predicates?.length) {
      // A group something above is about to remove has nothing to show: a nested one mid-cascade
      // (its last condition just went and _onDelete is calling up to have the group itself
      // dropped), or a root one whose caller opted into that same cascade.
      if (level > 1 || cascadeDeleteAtRoot) {
        return <div />;
      }
      // A plain root Filter never empties itself (_onDelete refills it), so this only happens when
      // a caller hands it an empty predicate list. Offer the way back in rather than a dead end.
      return <>{_getAddGroupCondition()}</>;
    }

    if (predicates?.predicates?.length <= 1) {
      const predicate: any = first(predicates?.predicates);

      if (predicate?.groupPredicateId) {
        return (
          <Filter
            name={name}
            key={`filter-${predicate?.groupPredicateId}`}
            displayMode={displayMode}
            onChange={_onChange}
            onDelete={_onDelete}
            leftRenderType={leftRenderType}
            lhsDisabled={lhsDisabled}
            value={predicate}
            level={level + 1}
            fieldValues={fieldValues}
            fetchPicklistValues={fetchPicklistValues}
            picklistValues={picklistValues}
            operatorType={operatorType}
            rightType={rightType}
            allowUserToken={allowUserToken}
            rightValues={rightValues}
            context={context}
            hideLeftInput={hideLeftInput}
            verticalLayout={verticalLayout}
            isAllDisabled={isAllDisabled}
            placeHolder={placeHolder}
            fieldValueOptions={fieldValueOptions}
            disableTokens={disableTokens}
            disableNesting={disableNesting}
            alwaysShowDelete={alwaysShowDelete}
          />
        );
      }
      return (
        <>
          <div className={cls} key={`filter-condition-container-${predicate?.predicateId}`}>
            <FilterCondition
              name={name}
              // No onAdd here: with a single condition there's nothing to nest with yet -
              // the nest icon would misleadingly suggest a group when it'd just add a sibling.
              // Adding a second condition (via the button below) is what enables nesting.
              // onDelete only at nested levels by default: deleting the root's only condition
              // would leave an empty filter, but deleting a nested group's last condition is how
              // you collapse that group entirely - it correctly cascades up via _onDelete below.
              // alwaysShowDelete opts a caller into keeping the icon here too (e.g. schema
              // studio's validation rules, which want a delete affordance on the lone condition).
              onDelete={level > 1 || alwaysShowDelete ? _onDelete : undefined}
              defaultValue={predicate}
              displayMode={displayMode}
              onChange={_onChange}
              lhsDisabled={lhsDisabled}
              leftRenderType={leftRenderType}
              fetchPicklistValues={fetchPicklistValues}
              picklistValues={picklistValues}
              fieldValues={fieldValues}
              operatorType={operatorType}
              rightType={rightType}
              singleCondition={singleCondition}
              allowUserToken={allowUserToken}
              rightValues={rightValues}
              context={context}
              hideLeftInput={hideLeftInput}
              verticalLayout={verticalLayout}
              allFieldsDisabled={isAllDisabled}
              placeHolder={placeHolder}
              fieldValueOptions={fieldValueOptions}
              disableTokens={disableTokens}
            />
          </div>
          {_getAddGroupCondition()}
        </>
      );
    } else if (predicates?.predicates?.length > 1) {
      // Show the group elements for multiple filters
      each(predicates?.predicates, (predicate) => {
        const itemKey = predicate.predicateId || predicate.groupPredicateId;
        const localPredicate = predicate;

        if (filters.length > 0 && isReadonly) {
          filters.push(
            <div key={`readonly-${itemKey}`} className="synri-readonly-operator">
              {operator}
            </div>
          );
        }

        if (predicate?.groupPredicateId) {
          filters.push(
            <Filter
              name={name}
              key={`filter-${predicate?.groupPredicateId}`}
              displayMode={displayMode}
              onChange={_onChange}
              onDelete={_onDelete}
              lhsDisabled={lhsDisabled}
              leftRenderType={leftRenderType}
              value={localPredicate}
              level={level + 1}
              fetchPicklistValues={fetchPicklistValues}
              picklistValues={picklistValues}
              fieldValues={fieldValues}
              operatorType={operatorType}
              rightType={rightType}
              allowUserToken={allowUserToken}
              rightValues={rightValues}
              context={context}
              hideLeftInput={hideLeftInput}
              verticalLayout={verticalLayout}
              isAllDisabled={isAllDisabled}
              placeHolder={placeHolder}
              fieldValueOptions={fieldValueOptions}
              disableTokens={disableTokens}
              disableNesting={disableNesting}
              alwaysShowDelete={alwaysShowDelete}
            />
          );
        } else {
          filters.push(
            <div className={cls} key={`filter-condition-container-${localPredicate.predicateId}`}>
              <FilterCondition
                name={name}
                onAdd={!disableNesting && level < MAX_LEVEL ? _onNestCondition : undefined}
                defaultValue={localPredicate}
                displayMode={displayMode}
                onChange={_onChange}
                onDelete={_onDelete}
                lhsDisabled={lhsDisabled}
                leftRenderType={leftRenderType}
                fetchPicklistValues={fetchPicklistValues}
                picklistValues={picklistValues}
                fieldValues={fieldValues}
                context={context}
                operatorType={operatorType}
                rightType={rightType}
                singleCondition={singleCondition}
                allowUserToken={allowUserToken}
                rightValues={rightValues}
                hideLeftInput={hideLeftInput}
                disableTokens={disableTokens}
                verticalLayout={verticalLayout}
                allFieldsDisabled={isAllDisabled}
                placeHolder={placeHolder}
                fieldValueOptions={fieldValueOptions}
              />
            </div>
          );
        }
      });
      const groupOperator = _getGroupOperator();
      let addFilterGroupBtn = _getAddGroupCondition();
      const groupCls = cx('group-filter-container', 'synri-filter-v2', {
        'root-group': level === 1,
        'synri-filter-readonly': isReadonly,
      });
      return (
        <div className={groupCls}>
          <div className="group-filter-body">
            {groupOperator}
            <div className="filter-group-groups">{filters}</div>
          </div>
          {addFilterGroupBtn}
        </div>
      );
    }
    return <div />;
  }
);

export default Filter;
